import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { flameWeekStore } from "../matching/flameWeekStore";
import { heartWeekStore } from "../matching/heartWeekStore";
import { HEARTS_PER_WEEK_GUEST, flamesPerWeek } from "../matching/weights";
import {
  billingStore,
  type BillingState,
} from "./billingStore";

function formatCredits(credits: number | null, unlimited: boolean): string {
  if (unlimited || credits == null) return "∞";
  return credits.toLocaleString();
}

function formatCount(n: number | null): string {
  return n == null ? "∞" : String(n);
}

/**
 * Game HUD: credits, hearts, flame. No paywall essay.
 * TWA charges stay Google Play only — no web checkout here.
 */
export function BillingBanner({
  userId,
  compact = false,
}: {
  userId: string | null;
  compact?: boolean;
}) {
  const t = useT();
  const [billing, setBilling] = useState<BillingState>(() => billingStore.get());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    return billingStore.subscribe(() => {
      setBilling(billingStore.get());
      setTick((n) => n + 1);
    });
  }, []);
  useEffect(() => {
    void billingStore.refresh(userId);
  }, [userId]);

  if (!userId) return null;
  void tick;

  const isGuest = userId.startsWith("user_guest_");
  const heartBudget = isGuest ? HEARTS_PER_WEEK_GUEST : billing.heartBudget;
  const flameBudget = isGuest ? 1 : flamesPerWeek(billing.plan);
  const heartsLeft = heartWeekStore.remaining(userId, heartBudget);
  const flamesLeft = flameWeekStore.remaining(userId, flameBudget);

  return (
    <aside
      className={`match-hud${compact ? " match-hud--compact" : ""}`}
      data-billing-explanatory="1"
      aria-label={t("match.hudAria")}
    >
      <span className="match-hud__chip" title={t("billing.creditsBalance", { count: formatCredits(billing.credits, billing.unlimitedCredits) })}>
        <span className="match-hud__coin" aria-hidden />
        {formatCredits(billing.credits, billing.unlimitedCredits)}
      </span>
      <span className="match-hud__chip" title={t("match.likeHeart")}>
        <em aria-hidden>♥</em>
        {formatCount(heartsLeft)}
      </span>
      <span className="match-hud__chip match-hud__chip--flame" title={t("match.flame")}>
        <em aria-hidden>🔥</em>
        {formatCount(flamesLeft)}
      </span>
    </aside>
  );
}
